<script setup>
import { computed, ref, onMounted } from 'vue'
import { useMainStore } from '@/stores/main'
import { mdiEye, mdiTrashCan, mdiCheckCircleOutline, mdiAlertCircleOutline, mdiHelpCircleOutline, mdiCloseCircleOutline } from '@mdi/js'
import CardBoxModal from '@/components/CardBoxModal.vue'
import TableCheckboxCell from '@/components/TableCheckboxCell.vue'
import BaseLevel from '@/components/BaseLevel.vue'
import BaseButtons from '@/components/BaseButtons.vue'
import BaseButton from '@/components/BaseButton.vue'
import BaseIcon from '@/components/BaseIcon.vue'
import UserAvatar from '@/components/UserAvatar.vue'
import { getDevices, getDevicesByList } from '@/orm'

defineProps({
  checkable: {
    type: Boolean,
    default: false
  },
  defaultPageSize: {
    type: Number,
    default: 20
  }
})

const mainStore = useMainStore()

const items = ref([])
const currentPage = ref(0)
const pageSize = ref(25)
const totalPages = ref(1)
const sortColumn = ref('name')
const sortOrder = ref('asc')
const loading = ref(true)
const isModalActive = ref(false)
const isModalDangerActive = ref(false)
const checkedRows = ref([])

//const items = computed(() => mainStore.clients)
/*
const emit = defineEmits(['menu-click'])

const menuClick = (event, item) => {
  emit('menu-click', event, item)
}
*/

const itemsPaginated = computed(() =>
  items.value.slice(pageSize.value * currentPage.value, pageSize.value * (currentPage.value + 1))
)

const numPages = computed(() => Math.ceil(items.value.length / pageSize.value))

const currentPageHuman = computed(() => currentPage.value + 1)

const pagesList = computed(() => {
  const pagesList = []

  for (let i = 0; i < numPages.value; i++) {
    pagesList.push(i)
  }

  return pagesList
})

const remove = (arr, cb) => {
  const newArr = []

  arr.forEach((item) => {
    if (!cb(item)) {
      newArr.push(item)
    }
  })

  return newArr
}

const checked = (isChecked, client) => {
  if (isChecked) {
    checkedRows.value.push(client)
  } else {
    checkedRows.value = remove(checkedRows.value, (row) => row.id === client.id)
  }
}

const formatDate = (date)  => {
  if (date)
    return (new Date(date.seconds * 1000)).toLocaleDateString()
  else
    return ''
}
const formatCurrency = (price)  => {
  return `$${price.toFixed(2)}`
}
const formatLevels = (levelItem)  => {
  if (levelItem != null) {
    return levelItem.map(num => `${num} %`).join(', ')
  } else {
    return ''
  }
}
const formatProgress = (levelItem)  => {
  if (levelItem != null && levelItem.length > 0) {
    let res = 0
    levelItem.map(num => res += num)
    return (res / levelItem.length).toFixed(0)
  } else {
    return 0
  }
}

const fetchData = async ()  => {
  let querySnapshot = null
  const customer = mainStore.customer
  if (customer) {
      const devicelist = mainStore.deviceList
      querySnapshot = await getDevicesByList(devicelist)
  } else {
      querySnapshot = await getDevices()
  }
  items.value = querySnapshot.map(doc => ({
      id: doc.id,
      ...doc.data(),
  }))
  loading.value = false
}

onMounted(() => {
  fetchData()
})

</script>

<template>
  <CardBoxModal v-model="isModalActive" title="Device details">
    <p><b>Device details is not possible at this moment.</b></p>
    <p>The work is ongoing.</p>
  </CardBoxModal>

  <CardBoxModal v-model="isModalDangerActive" title="Please confirm" button="danger" has-cancel>
    <p><b>Something bad hapened.</b></p>
    <p>Please, continue.</p>
  </CardBoxModal>

  <table>
    <thead>
      <tr>
        <th v-if="checkable" />
        <th>Status</th>
        <th>Name</th>
        <th>Customer</th>
        <th>Last data</th>
        <th>Levels</th>
        <th>Volume</th>
        <th>Alarm</th>
        <th />
      </tr>
    </thead>
    <tbody>
      <tr v-for="item in items" :key="item.id">
        <TableCheckboxCell v-if="checkable" @checked="checked($event, item)" />
        <td data-label="Status" class="justify-center">
          <BaseIcon v-if="item['LowFragrance'] === false && item['State'] == 3" :path="mdiCheckCircleOutline" color="sucess" size=30 />
          <BaseIcon v-if="item['LowFragrance'] === true && item['State'] == 3" :path="mdiAlertCircleOutline" color="warning" size=30 />
          <BaseIcon v-if="item['State'] != 3" :path="mdiHelpCircleOutline" color="info" size=30 />
        </td>
        <td data-label="Name">
          {{ item.id }}
        </td>
        <td data-label="Customer">
          {{ item["Customer"] }}
        </td>
        <td data-label="LastData">
          {{ formatDate(item['LastData']) }}
        </td>
        <td data-label="Levels">
          {{ formatLevels(item['UnitLevels']) }}
        </td>
        <td data-label="Volume" class="lg:w-32">
          <progress class="flex w-2/5 self-center lg:w-full" max="100" :value="formatProgress(item['UnitLevels'])">
            {{ formatProgress(item['UnitLevels']) }}
          </progress>
        </td>
        <td data-label="Alarms" class="justify-center">
          <BaseIcon v-if="item['AlarmStatus'] === 0" :path="mdiCheckCircleOutline" color="sucess" size=30 />
          <BaseIcon v-if="item['AlarmStatus'] === 1" :path="mdiAlertCircleOutline" color="warning" size=30 />
          <BaseIcon v-if="item['AlarmStatus'] === 2" :path="mdiCloseCircleOutline" color="error" size=30 />
          <BaseIcon v-if="item['AlarmStatus'] < 0 || item['AlarmStatus'] > 2" :path="mdiHelpCircleOutline" color="info" size=30 />
        </td>
        <td class="before:hidden lg:w-1 whitespace-nowrap">
          <BaseButtons type="justify-start lg:justify-end" no-wrap>
            <BaseButton color="info" :icon="mdiEye" small :to="`/devices/${item.id}`" /><!--@click="this.$router.push(`/devices/${item.id}`);"-->
          </BaseButtons>
        </td>
      </tr>
    </tbody>
  </table>
  <div class="p-3 lg:px-6 border-t border-gray-100 dark:border-slate-800">
    <BaseLevel>
      <BaseButtons>
        <BaseButton
          v-for="page in pagesList"
          :key="page"
          :active="page === currentPage"
          :label="page + 1"
          :color="page === currentPage ? 'lightDark' : 'whiteDark'"
          small
          @click="currentPage = page"
        />
      </BaseButtons>
      <small>Page {{ currentPageHuman }} of {{ numPages }}</small>
    </BaseLevel>
  </div>
</template>